import { Suspense, lazy } from 'react';
import { Head as Helmet } from 'vite-react-ssg';
import { US_HQ_SCHEMA } from '@/lib/geo/schema';
import { generateOrganizationSchema, generateWebSiteSchema, generateSpeakableSchema } from '@/utils/schemaGenerator';

import { Header } from '@/components/layout/Header';
import { HeroSection } from '@/components/home/HeroSection';
import { TrustBadges } from '@/components/home/TrustBadges';
import { SEO } from '@/components/seo/SEO';

const SPEAKABLE_SCHEMA = generateSpeakableSchema('https://ortemtech.com/', [
  'h1', '.hero-tagline', '.hero-description', '[data-speakable]', '.trust-badges'
]);

// Canonical Organization entity — emitted once on homepage so #organization @id
// is defined for all pages that reference it (US_HQ_SCHEMA.parentOrganization, etc.)
const ORGANIZATION_SCHEMA = generateOrganizationSchema();
const WEBSITE_SCHEMA = generateWebSiteSchema();

// Lazy load below-the-fold components
const Footer = lazy(() => import('@/components/layout/Footer').then(module => ({ default: module.Footer })));
const ServicesSection = lazy(() => import('@/components/home/ServicesSection').then(module => ({ default: module.ServicesSection })));
const CaseStudiesSection = lazy(() => import('@/components/home/CaseStudiesSection').then(module => ({ default: module.CaseStudiesSection })));
const WhyOrtemSection = lazy(() => import('@/components/home/WhyOrtemSection').then(module => ({ default: module.WhyOrtemSection })));
const TechStackSection = lazy(() => import('@/components/home/TechStackSection').then(module => ({ default: module.TechStackSection })));
const TestimonialsSection = lazy(() => import('@/components/home/TestimonialsSection').then(module => ({ default: module.TestimonialsSection })));
const CTASection = lazy(() => import('@/components/home/CTASection').then(module => ({ default: module.CTASection })));
const AboutSection = lazy(() => import('@/components/home/AboutSection').then(module => ({ default: module.AboutSection })));
const PricingPreview = lazy(() => import('@/components/home/PricingPreview').then(module => ({ default: module.PricingPreview })));
const LazyHomeFAQSection = lazy(() => import('@/components/home/HomeFAQSection').then(module => ({ default: module.HomeFAQSection })));



const Index = () => {
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(ORGANIZATION_SCHEMA)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(WEBSITE_SCHEMA)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(US_HQ_SCHEMA)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(SPEAKABLE_SCHEMA)}
        </script>
      </Helmet>
      <SEO page="home" />

      <div className="min-h-screen bg-background">
        <Header />
        <main>
          {/* Entity disambiguation microdata — GEO signal for AI crawlers */}
          <div
            itemScope
            itemType="https://schema.org/Organization"
            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, overflow: 'hidden' }}
            aria-hidden="true"
          >
            <meta itemProp="name" content="Ortem Technologies LLC" />
            <meta itemProp="alternateName" content="Ortem Technologies" />
            <meta itemProp="foundingDate" content="2012" />
            <meta itemProp="url" content="https://ortemtech.com" />
            <meta itemProp="numberOfEmployees" content="200" />
            <meta itemProp="description" content="Ortem Technologies LLC is a custom software and AI development company founded in 2012, headquartered in Lewes, Delaware, United States. The company has 200+ engineers and has delivered 1,000+ software projects for startups and enterprises across the USA, UK, Australia, UAE, and Canada. Services include mobile app development, web application development, AI/ML solutions, cloud infrastructure, IoT, and eCommerce. Rated 5.0/5 on Clutch and GoodFirms. 98% client retention rate." />
          </div>
          <HeroSection />
          <TrustBadges />
          {/* Entity paragraph — visible text for GEO/AI entity recognition */}
          <div className="sr-only">
            <p>
              Ortem Technologies LLC is a custom software and AI development company founded in 2012
              and headquartered in Lewes, Delaware, United States.
              The company employs 200+ engineers and has delivered over 1,000 software projects for
              startups and enterprises across the USA, UK, Australia, United Arab Emirates, and Canada.
              Services include mobile app development, web application development, AI and machine learning
              solutions, cloud infrastructure, IoT systems, and eCommerce development. Ortem Technologies
              is rated 5.0/5 on Clutch and GoodFirms with a 98% client retention rate.
            </p>
          </div>
          {/* <ClientLogosSection /> removed */}
          <Suspense fallback={<div className="h-96" />}>
            <ServicesSection />
          </Suspense>

          <Suspense fallback={<div className="h-96" />}>
            <AboutSection />
          </Suspense>

          <Suspense fallback={<div className="h-96" />}>
            <CaseStudiesSection />
          </Suspense>

          <Suspense fallback={<div className="h-96" />}>
            <WhyOrtemSection />
          </Suspense>

          <Suspense fallback={<div className="h-96" />}>
            <PricingPreview />
          </Suspense>

          <Suspense fallback={<div className="h-96" />}>
            <TechStackSection />
          </Suspense>

          <Suspense fallback={<div className="h-96" />}>
            <TestimonialsSection />
          </Suspense>

          <Suspense fallback={<div className="h-96" />}>
            <LazyHomeFAQSection />
          </Suspense>

          <Suspense fallback={<div className="h-48" />}>
            <CTASection />
          </Suspense>
        </main>
        <Suspense fallback={<div className="h-96" />}>
          <Footer />
        </Suspense>
      </div>
    </>
  );
};

export default Index;
